import { mergeMap, catchError, switchMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { EMPTY, Observable, of } from 'rxjs';
import get from 'lodash.get';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { GuidService } from 'ui-components/services/guid-service';
import { ModalService } from 'ui-components/services/modal-service';

import Global from '../../../store/global-selectors';
import NewOrderModal from '../../../orders/modals/new-order-modal';
import newOrderModalMessages from '../../../orders/modals/new-order-modal/messages';
import { CancelOrderModal } from '../../../components/modals/cancel-order-modal';
import { bulkLoadSalesOrders$ } from '../../bulkload/bulkload.observables';
import * as OrderActions from '../orders.actions';
import * as ODActions from '../../../store/orders/order-details/orders.details.actions';
import { createEmptyOrder } from '../../../services/empty-order-creator';
import * as Actions from './edit.order.actions';
import { orderCountSubject } from '../../../pages/home-page/hooks/use-order-import-data';

const messages = defineMessages({
  notificationTitle: {
    defaultMessage: 'ShipStation could not create the order',
    id: 't3hvIr',
    description: 'title informing the user that the order was not created',
  },
  notificationBody: {
    defaultMessage:
      'Your order matched an automation rule with a “Don’t import” action. Create a new order that does not match the rule or deactivate the rule and try again.',
    id: '6efs/w',
    description: 'description of why the order was not created having to do with a do not import automation rule',
  },
});

let modalId;

export const prepareToOpenNewOrderModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PREPARE_NEW_ORDER_MODAL_OPEN),
    mergeMap(() => {
      const state = getState();

      const homeCountry = Global.selectSellerHomeCountry(state);
      const defaultCurrency = Global.selectSellerDefaultCurrency(state);

      const emptyOrder = createEmptyOrder(homeCountry, defaultCurrency);

      const workflowGuid = GuidService.newGuid();

      return of(Actions.openNewOrderModal(emptyOrder, workflowGuid));
    }),
  );
};

export const openNewOrderModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.NEW_ORDER_MODAL_OPEN),
    switchMap(({ workflowGuid }) => {
      return new Observable((observer) => {
        modalId = ModalService.launchModal({
          component: NewOrderModal,
          height: '100%',
          maxHeight: '100%',
          portalProps: {
            confirmCancelBodyMessage: newOrderModalMessages.newOrderConfirmCancelBody,
            confirmCancelButtonMessage: newOrderModalMessages.newOrderConfirmCancelButton,
            confirmCancelHeaderMessage: newOrderModalMessages.newOrderConfirmCancelHeader,
            shouldConfirmOnClose: () => Global.selectEditOrdergetEditOrderHasMadeChanges(getState()),
          },
          props: { workflowGuid },
          onClose: () => {
            const hasMadeChanges = Global.selectEditOrdergetEditOrderHasMadeChanges(getState());
            observer.next(of(Actions.closedNewOrderModal(hasMadeChanges, workflowGuid)));
            observer.complete();
          },
        });
      }).pipe(mergeMap((next$) => next$));
    }),
  );
};

export const onChangesSaved = (action$) => {
  return action$.pipe(
    ofType(OrderActions.UPDATE_ORDER_SUCCESS),
    mergeMap(() => {
      ModalService.closeModalById(modalId);
      return of(OrderActions.silentRequestGetOrders());
    }),
  );
};

export const onSaveNewOrder = (action$) => {
  return action$.pipe(
    ofType(OrderActions.CREATE_ORDER_SUCCESS),
    mergeMap((action) => {
      const order = get(action, 'order');
      const salesOrderId = get(order, 'salesOrderId');
      const redirect = get(action, 'redirect');
      ModalService.closeModalById(modalId);

      return bulkLoadSalesOrders$([salesOrderId]).pipe(
        mergeMap(({ entities }) => {
          const { fulfillmentPlanIds } = entities?.salesOrders[salesOrderId] ?? {};
          const [fulfillmentPlanId] = fulfillmentPlanIds || [];
          const isCreateAnother = redirect === 'createAnother';
          const nextObservable$ = isCreateAnother ? of(Actions.prepareToOpenNewOrderModal()) : EMPTY;
          if (!fulfillmentPlanId) {
            NotificationService.showNotification({
              notificationType: NOTIFICATION_TYPE_ERROR,
              title: <FormattedMessage {...messages.notificationTitle} />,
              content: <FormattedMessage {...messages.notificationBody} />,
            });
            return nextObservable$;
          }
          orderCountSubject.next(entities);
          return isCreateAnother
            ? nextObservable$
            : of(
                ODActions.goToOrderDetails({
                  salesOrderId,
                  selectOrderInGrid: true,
                  fulfillmentPlanId,
                }),
              );
        }),
        catchError(() => {
          return EMPTY;
        }),
      );
    }),
  );
};

export const openCancelOrderModal = (action$) => {
  return action$.pipe(
    ofType(Actions.CANCEL_BULK_ORDER_MODAL_OPEN),
    mergeMap((action) => {
      ModalService.launchModal({
        component: CancelOrderModal,
        props: {
          shouldShowPendingFulfillmentText: action.shouldShowPendingFulfillmentText,
        },
      });
      return EMPTY;
    }),
  );
};
